/**
 * Trait validation utilities for character creation
 *
 * Validates trait assignments according to SRD rules:
 * - All 6 traits must have assignments
 * - Must use exactly: [+2, +1, +1, 0, 0, -1]
 * - Each modifier used exactly once
 */

export interface ValidationState {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  hints: string[];
}

export interface TraitAssignment {
  agility: number | null;
  strength: number | null;
  finesse: number | null;
  instinct: number | null;
  presence: number | null;
  knowledge: number | null;
}

// The exact set of modifiers that must be distributed
export const AVAILABLE_MODIFIERS = [2, 1, 1, 0, 0, -1];

// Trait descriptions from SRD
export const TRAIT_DESCRIPTIONS = {
  agility: {
    name: 'Agility',
    actions: ['Sprint', 'Leap', 'Maneuver'],
    description: 'Fast, nimble, quick reactions'
  },
  strength: {
    name: 'Strength',
    actions: ['Lift', 'Smash', 'Grapple'],
    description: 'Physical prowess and stamina'
  },
  finesse: {
    name: 'Finesse',
    actions: ['Control', 'Hide', 'Tinker'],
    description: 'Precision, dexterity, and coordination'
  },
  instinct: {
    name: 'Instinct',
    actions: ['Perceive', 'Sense', 'Navigate'],
    description: 'Intuition, awareness, and survival'
  },
  presence: {
    name: 'Presence',
    actions: ['Charm', 'Perform', 'Deceive'],
    description: 'Charisma, influence, and expression'
  },
  knowledge: {
    name: 'Knowledge',
    actions: ['Recall', 'Analyze', 'Comprehend'],
    description: 'Learning, reasoning, and understanding'
  }
};

/**
 * Calculate remaining modifiers based on current assignments
 */
export function calculateRemainingModifiers(assignments: TraitAssignment): number[] {
  const assigned = Object.values(assignments).filter((v): v is number => v !== null);
  const remaining = [...AVAILABLE_MODIFIERS];

  // Remove each assigned value from the remaining array
  for (const value of assigned) {
    const index = remaining.indexOf(value);
    if (index !== -1) {
      remaining.splice(index, 1);
    }
  }

  return remaining.sort((a, b) => b - a);
}

/**
 * Validate trait assignments according to SRD rules
 */
export function validateTraitAssignments(assignments: TraitAssignment): ValidationState {
  const errors: string[] = [];
  const warnings: string[] = [];
  const hints: string[] = [];

  // Check all traits assigned
  const assignedValues = Object.values(assignments).filter((v): v is number => v !== null);
  const unassignedCount = 6 - assignedValues.length;

  if (unassignedCount > 0) {
    errors.push(`${unassignedCount} trait${unassignedCount === 1 ? '' : 's'} still ${unassignedCount === 1 ? 'needs' : 'need'} a modifier`);

    if (assignedValues.length === 0) {
      hints.push('Try using your class suggested traits to get started!');
    } else if (unassignedCount === 1) {
      hints.push('Almost there! Just one more trait to assign.');
    } else {
      hints.push(`${unassignedCount} more to go!`);
    }

    return {
      isValid: false,
      errors,
      warnings,
      hints
    };
  }

  // Check correct modifier usage
  const sortedAssigned = [...assignedValues].sort((a, b) => b - a);
  const sortedRequired = [...AVAILABLE_MODIFIERS].sort((a, b) => b - a);

  const matchesRequired = JSON.stringify(sortedAssigned) === JSON.stringify(sortedRequired);

  if (!matchesRequired) {
    errors.push('Modifiers must be exactly: +2, +1, +1, 0, 0, -1');
    return {
      isValid: false,
      errors,
      warnings,
      hints
    };
  }

  // All validation passed
  hints.push('All traits assigned! You\'re ready to continue.');

  return {
    isValid: true,
    errors,
    warnings,
    hints
  };
}

/**
 * Validate that all modifiers are used correctly (no duplicates beyond what's allowed)
 */
export function checkModifierUsage(assignments: TraitAssignment): {
  valid: boolean;
  duplicates: number[];
} {
  const assigned = Object.values(assignments)
    .filter((v): v is number => v !== null)
    .sort((a, b) => b - a);

  const required = [...AVAILABLE_MODIFIERS].sort((a, b) => b - a);

  // Check if the sorted arrays match
  if (JSON.stringify(assigned) !== JSON.stringify(required)) {
    // Find which modifiers are duplicated
    const duplicates: number[] = [];
    const counts = new Map<number, number>();

    for (const modifier of required) {
      counts.set(modifier, (counts.get(modifier) || 0) + 1);
    }

    for (const assigned of Object.values(assignments).filter((v): v is number => v !== null)) {
      const count = counts.get(assigned) || 0;
      counts.set(assigned, count - 1);
    }

    for (const [modifier, count] of Array.from(counts.entries())) {
      if (count < 0) {
        duplicates.push(modifier);
      }
    }

    return {
      valid: false,
      duplicates
    };
  }

  return {
    valid: true,
    duplicates: []
  };
}

/**
 * Format a modifier value for display (+2, +1, 0, -1)
 */
export function formatModifier(value: number | null): string {
  if (value === null) return 'Unassigned';
  return value > 0 ? `+${value}` : `${value}`;
}

/**
 * Get all trait keys in a consistent order
 */
export function getTraitKeys(): (keyof TraitAssignment)[] {
  return ['agility', 'strength', 'finesse', 'instinct', 'presence', 'knowledge'];
}
